/**
 * Diamond info overlay
 *
 * Answers "why is this candle a diamond?" AND "why isn't it?" for whichever
 * bar the crosshair is on. Both halves matter: a rule you can only see when it
 * fires is a rule you can't tune.
 *
 * Laid out as two dense lines rather than a panel — it sits over the chart, so
 * every row it takes is price you can't see:
 *
 *   DIA 21:06 L                                   56.0 MODERATE
 *   body 56≥50   child 39≤40   atr .94≥.68
 *
 * BOTH answers come from the engine. This overlay used to call `evaluateCandle`
 * — re-running the whole rule client-side, complete with its own ATR series and
 * its own walk of the session extremes — to fill in these two lines. That copy
 * had drifted from the engine's (it never reset the extremes at the 5:02pm CT
 * roll, among other things), so the overlay could and did print a different
 * verdict from the marker sitting on the same bar. There is no local evaluation
 * left: a bar shows the engine's diamond, the engine's rejection, or an honest
 * statement that the engine was never asked about it.
 *
 * That last state is not rare and must not read as "not a setup" — nor as a
 * fault. It is mostly the bars newer than the last history fetch, which is
 * issued on mount and on a handful of events rather than per bar; a bar the
 * engine truly could not judge arrives as a REJECTION naming the missing
 * trailing data, not as silence. Saying "not judged" over the silence, on the
 * strength of a window-level counter, is what #371 was.
 *
 * The one thing NOT taken from the engine is the signal bar's OHLC, and for the
 * same reason everything else is: one copy. `SetupCandidate` carries no OHLC,
 * and the fix is not to put it on the wire — that would be a second copy of a
 * bar the chart is already drawing, free to disagree with the candle under the
 * diamond. It is looked up out of `marketStore.candles` by timestamp instead,
 * so the row and the candle are literally the same object. When that bar is not
 * in the loaded window (scrolled past the backfill, or trimmed) the row is
 * absent — same rule as the trend lines above: a missing value is honest, a
 * fabricated one is not.
 */

import React, { useMemo } from 'react';
import { useCandidateStore } from '../../store/candidateStore';
import { useMarketStore } from '../../store/marketStore';
import { findBarAt } from '../../utils/findBarAt';
import { dollarsPerPoint, outcomeForSignal } from '../../utils/diamondPositionModels';
import type {
  CandidateThresholds,
  EngineDiamond,
  EngineRejectedBar,
} from '../../utils/setupCandidates';
import type { HistoryCoverage, HistoryStatus } from '../../store/candidateStore';
import './DiamondInfoOverlay.css';

interface DiamondInfoOverlayProps {
  /** The candle under the crosshair, or the latest when not hovering. */
  candle: { timestamp: number } | null;
  /** The engine's diamond for that bar, when the rule fired on it. */
  diamond: EngineDiamond | null;
  /** The engine's rejection for that bar, when the rule threw it out. */
  rejection: EngineRejectedBar | null;
  /** The engine's rule, for printing the threshold beside the measurement. */
  thresholds: CandidateThresholds | null;
  /** Where the history fetch got to, so an empty overlay can say why. */
  status: HistoryStatus;
  error: string | null;
  coverage: HistoryCoverage | null;
}

/** Drop the leading zero: 0.94 -> .94, which buys a character per number. */
const terse = (n: number) => n.toFixed(2).replace(/^0\./, '.');

/**
 * Every price in this strip, in one place.
 *
 * Two decimals is what the sim line has always printed for entry and exit, so
 * the OHLC row uses the same call rather than inventing a rival one — a second
 * format is how two rows come to render the same number differently. Named and
 * shared so that if it ever needs to follow the instrument's tick size, it
 * changes in one place and the whole strip moves together.
 */
const price = (n: number) => n.toFixed(2);

/**
 * Why this bar has no verdict, in words the reader can act on.
 *
 * Four different silences that used to be one. With a local scorer every bar
 * got an answer, so "blank" only ever meant "not a setup"; now blank can also
 * mean the fetch is in flight, the engine is unreachable, or the bar is newer
 * than the last snapshot — and confusing those is how someone concludes the
 * rule stopped firing when the engine is simply down.
 *
 * Every line here is a statement about THIS BAR, and that is the whole of the
 * fix in #371. This function used to end on a window-level statistic dressed as
 * a per-bar verdict: `candlesJudged < candlesInWindow` printed "not judged — no
 * trailing data" for whatever bar the crosshair was on, having never looked at
 * that bar. The oldest bar of any window is unjudgeable — nothing precedes it
 * to build a trailing bar from — so the condition was true at every depth
 * measured on prod (1 of 1082, 1 of 2464, 1 of 3846, 1 of 5229), the branch
 * fired on essentially every bar, and an ordinary quiet candle read as a broken
 * engine. It cost a session of hunting a backend fault that was never there.
 *
 * A bar the engine genuinely could not judge does not come through here at all:
 * `evaluate` returns an outcome for EVERY bar in the window, so a missing
 * trailing bar arrives as a REJECTION carrying its own reason (`noTrailingBar`,
 * `trailingBarInvalid`) and renders as the engine's own sentence two branches
 * down. Measured on prod today: 83 diamonds + 1163 rejections = the full 1246
 * bars in window, the one unjudgeable bar among the rejections. So "not judged"
 * is never this function's to say.
 */
function absenceReason(
  status: HistoryStatus,
  error: string | null,
  coverage: HistoryCoverage | null,
  timestamp: number
): string {
  if (status === 'loading') return 'loading engine scores…';
  if (status === 'error') return `engine unavailable — ${error ?? 'fetch failed'}`;
  if (status === 'idle') return 'engine not asked yet';
  if (coverage?.fromMs != null && timestamp < coverage.fromMs) {
    return `older than the ${coverage.days}-session window the engine was asked for`;
  }
  // THE COMMON CASE, and it is an ordinary one. The history fetch is issued on
  // mount, symbol switch, lookback change, recalc and reconnect — deliberately
  // not per bar — and between those the `diamond-signal` socket carries HITS
  // only. So every bar that has completed since the last fetch, plus the bar
  // still forming, has nothing in either map whether the rule liked it or not,
  // and silence here means "no diamond has been reported for it". Stated as
  // what is known rather than as a gap: there is no fault in this state and the
  // words must not suggest one.
  if (coverage != null && timestamp > coverage.throughMs) {
    return 'no diamond on this bar — live bars report hits only';
  }
  // Inside the snapshot and still blank. Per the note above the engine answers
  // for every bar it was asked about, so this is the response having nothing at
  // all for a bar it covered — rare, unexplained, and left as its own sentence
  // rather than guessing a cause for it.
  return 'no engine verdict for this bar';
}

/**
 * How much of the window has to be blind before it is worth saying so.
 *
 * Coverage is a property of the WINDOW, so it gets its own line rather than
 * being folded into the per-bar sentence above — that conflation is #371 — and
 * it only earns that line when the hole is real.
 *
 * Every window has one unjudgeable bar at its oldest edge, and on prod that is
 * the whole of it at every depth: 1 of 1082 at 3 sessions, 1 of 2464 at 5, 1 of
 * 3846 at 7, 1 of 5229 at 10. A warning that fires on 1 in 2464 fires always,
 * which is the same as not warning at all.
 *
 * The holes that ARE worth a warning are structural — trailing disabled, an
 * unreadable SCID file, bars older than the front month's first tick — and each
 * takes out whole sessions at a time. One session is about a third of a
 * 3-session window: hundreds of bars, not one. Nothing lands between "one
 * boundary bar" and "a lost session", so the threshold only has to sit
 * somewhere in that empty gap. 5% is two orders of magnitude above the boundary
 * bar and well below a single lost session.
 */
const BLIND_SPOT_WARN_FRACTION = 0.05;

/** The window's blind spot, or null when it is just the boundary bar. */
function blindSpotNote(coverage: HistoryCoverage | null): string | null {
  if (!coverage || coverage.candlesInWindow <= 0) return null;
  const blind = coverage.candlesInWindow - coverage.candlesJudged;
  if (blind <= 0) return null;
  if (blind < coverage.candlesInWindow * BLIND_SPOT_WARN_FRACTION) return null;
  // Names the cause, because at this size there is only one: `candlesJudged`
  // counts bars with a valid trailing bar, so the missing ones had none.
  return `engine blind on ${blind} of ${coverage.candlesInWindow} bars in this window (no trailing data)`;
}

const DiamondInfoOverlay: React.FC<DiamondInfoOverlayProps> = React.memo(
  ({ candle, diamond, rejection, thresholds, status, error, coverage }) => {
    // BEFORE the early returns below: hooks cannot be called conditionally.
    //
    // The SAME walk the stats overlay uses. Two implementations of "what did
    // the policy do with this position" is how the popup came to call a
    // flipped-out trade `open` while the scoreboard had already closed it.
    const engineDiamonds = useCandidateStore((st) => st.engineDiamonds);
    const policy = useMemo(() => {
      if (!diamond?.simulated) return null;
      const sorted = Object.values(engineDiamonds)
        .filter((d): d is EngineDiamond => !!d && !!d.simulated)
        .sort((a, b) => a.timestamp - b.timestamp);
      // `flip` regardless of the bot's current policy: this line answers "what
      // would reversing have done with it", which is worth seeing either way.
      return outcomeForSignal(sorted, 'flip', dollarsPerPoint(sorted), diamond.timestamp);
    }, [engineDiamonds, diamond]);

    /**
     * The signal bar itself, out of the series the chart is drawing.
     *
     * Keyed off the engine's timestamp rather than the crosshair candle so the
     * OHLC is unambiguously the bar the verdict is about.
     *
     * Memoised, and a binary search inside that: `candles` runs to thousands of
     * bars and is replaced on every tick, so a bare `.find()` here would be a
     * full scan on each of them. `null` when the bar is not loaded, which the
     * row below treats as "print nothing".
     */
    const candles = useMarketStore((st) => st.candles);
    const signalBarTimestamp = diamond?.timestamp ?? rejection?.timestamp ?? null;
    const signalBar = useMemo(
      () => (signalBarTimestamp === null ? null : findBarAt(candles, signalBarTimestamp)),
      [candles, signalBarTimestamp]
    );

    if (!candle) {
      return (
        <div className="diamond-info-overlay">
          <span className="dio-tag">DIA</span>
          <span className="dio-muted">no candle</span>
        </div>
      );
    }

    if (!diamond && !rejection) {
      // Two spans, two scopes: the first answers "why is there nothing on THIS
      // bar", the second reports the window's coverage and only when the hole
      // is big enough to mean something. Printing the second as though it were
      // the first is exactly what this overlay used to do — see absenceReason.
      const blind = blindSpotNote(coverage);
      return (
        <div className="diamond-info-overlay">
          <span className="dio-tag">DIA</span>
          <span className="dio-muted">
            {absenceReason(status, error, coverage, candle.timestamp)}
          </span>
          {blind && <span className="dio-muted">{blind}</span>}
        </div>
      );
    }

    // One or the other, never both — the engine's `BarOutcome` is a sum type.
    const timestamp = diamond?.timestamp ?? rejection!.timestamp;
    const direction = diamond?.direction ?? rejection?.direction ?? null;
    const parentBodyPct = diamond?.parentBodyPct ?? rejection?.parentBodyPct ?? null;
    const childBodyPct =
      diamond?.childBodyPctOfParentBody ?? rejection?.childBodyPctOfParentBody ?? null;
    const atrRatio = diamond?.atrRatio ?? rejection?.atrRatio ?? null;

    const time = new Date(timestamp).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });

    /**
     * A measurement chip.
     *
     * `pass` is the ENGINE's word, not a comparison done here. Re-deriving
     * `value >= threshold` on this side would be a four-line reimplementation of
     * the conditions — small, plausible, and free to disagree with the rejection
     * reason printed two chips along. So an accepted diamond's chips all pass by
     * definition, and a rejected bar's failing chip is the one the engine named.
     */
    const Chip = ({
      pass,
      label,
      body,
    }: {
      pass: boolean;
      label: string;
      body: string;
    }) => (
      <span className={`dio-chip ${pass ? 'pass' : 'fail'}`}>
        <span className="dio-mark">{pass ? '✓' : '✗'}</span>
        {label}
        <span className="dio-num">{body}</span>
      </span>
    );

    /** Which measurement the engine's reject reason points at. */
    const failed = (metric: 'body' | 'child' | 'atr'): boolean => {
      const r = rejection?.reason;
      if (!r) return false;
      if (metric === 'body') return r === 'parentBodyTooSmall';
      if (metric === 'child') return r === 'childBodyTooLarge' || r === 'childBodyTooSmall';
      return r === 'atrRatioTooLow';
    };

    return (
      <div className="diamond-info-overlay">
        {/* Line 1 — what and how good */}
        <div className="dio-line dio-line-1">
          <span className="dio-tag">DIA</span>
          <span className="dio-when">
            {time} {direction === 'short' ? 'S' : direction === 'long' ? 'L' : '-'}
          </span>
          <span className={`dio-verdict ${diamond ? `g-${diamond.grade}` : 'g-none'}`}>
            {diamond ? (
              <>
                <b>{diamond.score.toFixed(1)}</b> {diamond.grade.replace('-', ' ')}
              </>
            ) : (
              'no candidate'
            )}
          </span>
        </div>

        {/* Line 2 — the engine's measurements, against the engine's rule */}
        <div className="dio-line">
          {parentBodyPct !== null && (
            <Chip
              pass={!failed('body')}
              label="body"
              body={`${parentBodyPct.toFixed(0)}${
                thresholds ? `≥${thresholds.parentBodyMinPct}` : ''
              }`}
            />
          )}
          {childBodyPct !== null && (
            <Chip
              pass={!failed('child')}
              label="child"
              body={`${childBodyPct.toFixed(0)}${
                thresholds ? `≤${thresholds.childBodyMaxPctOfParentBody}` : ''
              }`}
            />
          )}
          {atrRatio !== null && (
            <Chip
              pass={!failed('atr')}
              label="atr"
              body={`${terse(atrRatio)}${thresholds ? `≥${terse(thresholds.atrMult)}` : ''}`}
            />
          )}
          {/* The engine's own sentence for the failure, which carries the
              number that lost and the threshold it lost to. Shown for every
              rejection including the ones with no measurements at all (no
              trailing bar, ATR not warm), where the chips above are empty and
              this is the entire answer. */}
          {rejection && <span className="dio-muted">{rejection.detail}</span>}
          {/* Not a gate: proximity to the directional extreme DOCKS the score
              rather than rejecting the bar. Quoted from the engine's payload —
              the number it actually subtracted. */}
          {diamond && diamond.hodLodPenalty > 0 && (
            <span className="dio-chip dock">
              <span className="dio-mark">!</span>
              {diamond.direction === 'short' ? 'hod' : 'lod'}
              <span className="dio-dock">−{diamond.hodLodPenalty.toFixed(0)}</span>
            </span>
          )}
        </div>

        {/* The signal bar's own OHLC.
            Absent, not zeroed, when the bar is not in the loaded window — a
            diamond can sit further back than the candles the chart holds, and
            four dashes in the shape of prices would read as a flat bar rather
            than as no answer. Styled as context: labels in the same dim
            treatment as the DIA tag, values in the shared `dio-num`, no chip
            and no mark, because nothing here passed or failed anything. */}
        {signalBar && (
          <div className="dio-line dio-ohlc">
            <span className="dio-ohlc-pair">
              <span className="dio-ohlc-label">O</span>
              <span className="dio-num">{price(signalBar.open)}</span>
            </span>
            <span className="dio-ohlc-pair">
              <span className="dio-ohlc-label">H</span>
              <span className="dio-num">{price(signalBar.high)}</span>
            </span>
            <span className="dio-ohlc-pair">
              <span className="dio-ohlc-label">L</span>
              <span className="dio-num">{price(signalBar.low)}</span>
            </span>
            <span className="dio-ohlc-pair">
              <span className="dio-ohlc-label">C</span>
              <span className="dio-num">{price(signalBar.close)}</span>
            </span>
          </div>
        )}

        {/* Line 3 — the run this bar fired into.
            CONTEXT, and styled to read as context: no pass/fail marks and no
            deduction, because none of it touches the score. It was measured
            before it was shown — across 772 diamonds the counter-trend label
            predicted nothing at any lookback from 10 to 60 minutes — so it is
            here to be read, not obeyed. Anything that made it look like a
            verdict would be claiming more than the data supports. */}
        {/* Line 4 — what it would have done.
            FORWARD LOOKING, and separated from the measurement chips above for
            that reason: those are what the engine knew at the time, this is
            hindsight. Nothing here feeds the score. */}
        {diamond?.simulated && (
          <div className="dio-line dio-sim">
            {/* UNFILLED is NOT open. A resting entry whose limit was never
                touched before it expired never became a trade, and collapsing
                it into OPEN made expired orders read as positions still
                running -- the exact confusion this line caused. */}
            <span className={`dio-sim-result ${diamond.simulated.result}`}>
              {diamond.simulated.result === 'win'
                ? 'WIN'
                : diamond.simulated.result === 'loss'
                  ? 'LOSS'
                  : diamond.simulated.result === 'unfilled'
                    ? 'UNFILLED'
                    : 'OPEN'}
            </span>
            <span className="dio-muted">
              {diamond.simulated.points >= 0 ? '+' : ''}
              {diamond.simulated.points.toFixed(0)}pt
              {' · '}
              {diamond.simulated.dollars >= 0 ? '+' : '-'}$
              {Math.abs(diamond.simulated.dollars).toFixed(0)}
              {diamond.simulated.quantity > 1 ? ` (x${diamond.simulated.quantity})` : ''}
            </span>
            <span className="dio-muted">
              tp{diamond.simulated.tpPoints.toFixed(0)}/sl
              {diamond.simulated.slPoints.toFixed(0)} @ {price(diamond.simulated.entry)}
            </span>
            {/* What REVERSING would have done with it, which routinely differs
                from the bracket above: a flip closes at the opposing signal
                instead of waiting for TP or SL. Absent when this signal never
                opened a position — absorbed, skipped, or never filled. */}
            {policy && (
              <span
                className="dio-policy"
                title="Under flip: closed at the opposing signal rather than the bracket"
              >
                flip{' '}
                <span className={policy.won ? 'win' : 'loss'}>
                  {policy.points >= 0 ? '+' : ''}
                  {policy.points.toFixed(1)}pt
                </span>
                {policy.closedBy === 'reversal' && policy.closedAt !== null
                  ? ` @ ${price(policy.closedAt)}`
                  : ' (bracket)'}
              </span>
            )}
            {/* Band membership, not a claim that the bot traded it. */}
            {diamond.simulated.botName && (
              <span className="dio-muted" title="This signal falls in that bot's band">
                {diamond.simulated.botName}
              </span>
            )}
          </div>
        )}

        {diamond?.trend && (
          <div className="dio-line dio-trend">
            {/* Both quality numbers, because they answer different questions
                and can disagree: `agree` is how many bars pulled the same way,
                R² is whether the path was a line. A cliff followed by drift
                reads 100% and 0.6. */}
            <span className="dio-muted">
              {diamond.trend.runPoints < 0 ? 'down' : 'up'} {diamond.trend.runBars}b{' '}
              {diamond.trend.runPoints >= 0 ? '+' : ''}
              {diamond.trend.runPoints.toFixed(1)}pt{' '}
              {(diamond.trend.agree * 100).toFixed(0)}% R²
              {diamond.trend.r2.toFixed(2)}
            </span>
            {diamond.trend.counterTrend ? (
              <>
                <span className="dio-chip dock">
                  <span className="dio-mark">!</span>counter
                </span>
                {/* Two readings of displacement, structural first. `swept` is
                    how many bars back the signal reached — the thing the eye
                    checks — and `took back` is the same question in points. The
                    2026-08-06 03:46 bar reads 2/10 and 32%: it cleared two
                    candles and a third of the run, scored 78 "strong", and went
                    124 points the other way. */}
                <span className="dio-muted">
                  swept {diamond.trend.sweptBars}/{diamond.trend.runBars} · took back{' '}
                  {(diamond.trend.recovered * 100).toFixed(0)}%
                  {diamond.trend.closedBeyond ? '' : ', closed inside'}
                </span>
              </>
            ) : (
              <span className="dio-muted">with trend</span>
            )}
          </div>
        )}

        {diamond?.trendAlignment && (
          <div className="dio-line dio-align">
            {/* One line, ordered by how much each part decides.

                `body` is half the rule. The other half is the RUN, which the
                `dio-trend` line above already prints as `counter` — repeating
                it here would spend the row's width saying the same thing
                twice. Stack and the standing lines are rival rules recorded
                for the forward test; they decide nothing today, so they come
                last and stay muted.

                The "watching" chip is not decoration. `wouldSkip` is a shadow
                verdict and the bot took this signal anyway — without the word,
                a chip on a losing trade reads as "the filter caught it", which
                would be a claim the data does not support. */}
            <span className="dio-muted">
              body {diamond.trendAlignment.emasThroughBody}/3
              {' · stack '}
              {/* Three-way, matching the chart's stackTag(): an exact tie
                  (ema9 === ema21) sets neither flag and has no stance to
                  report. This branched on `stackOpposed` alone and printed
                  `with` on that tie — the reassuring reading, invented, while
                  the chart line correctly drew nothing. Two surfaces
                  disagreeing about the same bar is the thing this row is
                  supposed to settle. Opposed is tested first for the same
                  reason stackTag does it: a self-contradicting pair should
                  read as the warning, not the reassurance. */}
              {diamond.trendAlignment.variants?.stackOpposed
                ? 'against'
                : diamond.trendAlignment.stackAligned
                  ? 'with'
                  : '-'}
              {' · '}
              {(() => {
                const off = [
                  !diamond.trendAlignment.withVwap && 'vwap',
                  !diamond.trendAlignment.withEma200 && 'ema200',
                ].filter(Boolean);
                return off.length ? `off ${off.join('+')}` : 'on vwap+ema200';
              })()}
            </span>
            <span
              className={`dio-chip shadow ${
                diamond.trendAlignment.wouldSkip ? 'skip' : 'take'
              }`}
              title={
                'Shadow filter — records only, changes nothing. Would skip when ' +
                'the run disagrees (shown on the line above) or 2+ EMAs cut the ' +
                'candle body. Stack, VWAP and EMA200 are rival rules recorded for ' +
                'a forward test and do not decide. Nothing here has cleared its ' +
                'evidence bar on data that was not already searched.'
              }
            >
              watching: would {diamond.trendAlignment.wouldSkip ? 'skip' : 'take'}
            </span>
          </div>
        )}
      </div>
    );
  }
);

DiamondInfoOverlay.displayName = 'DiamondInfoOverlay';

export default DiamondInfoOverlay;
